import ArrowRight16 from '@carbon/icons-react/es/arrow--right/16';
import { Form, Space } from 'antd';
import React, { FC, useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useMutation } from 'react-query';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { UserAPI } from 'api/user';
import { Button } from 'components/atoms/button';
import {
  Container,
  ErrorMessage,
  Heading,
  LinkText,
  UserInfo,
} from 'components/atoms/form-elements';
import { Input } from 'components/atoms/input';
import { Layout } from 'components/atoms/layout';
import { PageLoading } from 'components/atoms/loading';
import { RoutePaths } from 'constant';
import { useTranslation } from 'hooks/use-translation';
import { useAccountMetaQuery } from 'queries';
// eslint-disable-next-line import/no-internal-modules
import { CompanyLogo } from 'routes/auth/common';
import { logger } from 'services';
import { SuccessPage } from './success-page';

const RecoveryButton = styled(Button)`
  margin-top: var(--spacing-03);
`;

export const ForgotPasswordPage: FC = () => {
  const [form] = Form.useForm();
  const [emailValue, setEmailValue] = useState<string>('');
  const [isResetEmailSent, setIsResetEmailSent] = useState<boolean>(false);

  // eslint-disable-next-line consistent-return
  const triggerResetMail = async () => {
    try {
      const { email } = await form.validateFields();

      return await UserAPI.triggerResetPasswordEmail(email)
        .then(() => {
          setEmailValue(email);
          setIsResetEmailSent(true);
        })
        .catch((err) => Promise.reject(err));
    } catch (error) {
      logger.log('password reset failed', { error });
    }
  };

  const {
    mutateAsync: handleClick,
    isLoading: resetMailSending,
    isError: isResetFailed,
  } = useMutation(triggerResetMail);

  const onSubmit = useCallback(() => {
    handleClick();
  }, [handleClick]);

  const { data, isLoading, isError } = useAccountMetaQuery();

  const translatedPlaceholderText = useTranslation('forgot_password.email');
  const requiredText = useTranslation('forgot_password.email_required');

  const renderScreen = () => {
    if (isLoading) {
      return <PageLoading />;
    }
    if (isResetEmailSent) {
      return <SuccessPage email={emailValue} />;
    }
    if (data && !isError) {
      return (
        <Layout>
          <Container>
            <Space direction="vertical" size={16}>
              {data && <CompanyLogo data={data.company} />}
              <Heading>
                <FormattedMessage id="forgot_password.page_title" />
              </Heading>
              <Form
                form={form}
                layout="vertical"
                onSubmitCapture={onSubmit}
                onFinish={onSubmit}
              >
                <Form.Item
                  name="email"
                  label={translatedPlaceholderText}
                  rules={[
                    {
                      required: true,
                      message: requiredText,
                    },
                  ]}
                >
                  <Input type="email" placeholder={translatedPlaceholderText} />
                </Form.Item>
                <ErrorMessage>
                  {isResetFailed
                    ? 'Something went wrong. Please try again.'
                    : ''}
                </ErrorMessage>
                <RecoveryButton
                  loading={resetMailSending}
                  type="primary"
                  icon={<ArrowRight16 />}
                  iconPlacement="right"
                  onClick={onSubmit}
                >
                  <FormattedMessage id="forgot_password.cta" />
                </RecoveryButton>
                <UserInfo>
                  <FormattedMessage
                    id="forgot_password.cta_back"
                    values={{
                      a: (chunks) => (
                        <Link to={RoutePaths.login}>
                          <LinkText>{chunks}</LinkText>
                        </Link>
                      ),
                    }}
                  />
                </UserInfo>
              </Form>
            </Space>
          </Container>
        </Layout>
      );
    }

    return <></>;
  };

  return renderScreen();
};
